import React, { memo, useState, useCallback } from 'react'
import CertificationModal from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/CertificationModal'
import { CERTIFICATION_CODE_LABELS } from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/constants'
import { TCertificationSchema } from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/schema/CertificationSchema'
import { getCertificationDefaultValues } from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/utils/getCertificationDefaultValues'
import EditIcon from 'transfix-icons/build/EditIcon'
import { Button } from 'transfix-ui/components/Button'
import Card from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { makeStyles } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { brandColors } from 'transfix-ui/theme/colors'
import { formatDate } from 'utils/formatDatetime'
import { useModalState } from 'utils/hooks'
import { ICertificationFragment } from './graphql/CertificationFragment'
import { IMotorCarrierCertificationsFragment } from './graphql/MotorCarrierCertificationsFragment'
import { useDiverseCarrierCertificationsSubmitter } from './hooks/useDiverseCarrierCertificationsSubmitter'

export const useDiverseCarrierCertificationsCardStyles = makeStyles(theme => ({
  header: {
    paddingBottom: theme.spacing(2.5),
  },
  certification: {
    borderTop: `1px solid ${brandColors.coolGray2}`,
    padding: `${theme.spacing(2)} 0`,

    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
  certificationEdit: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',

    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-start',
    },
  },
  iconButtonWrapper: {
    color: brandColors.skyBlue6,
    marginLeft: theme.spacing(1),
  },
  topBorder: {
    borderTop: `1px solid ${brandColors.coolGray2}`,
    paddingTop: theme.spacing(3),
  },
}))

export interface IDiverseCarrierCertificationsCardProps
  extends IMotorCarrierCertificationsFragment {}

const DiverseCarrierCertificationsCard = memo<IDiverseCarrierCertificationsCardProps>(
  ({ certifications, id: motorCarrierId }) => {
    const classes = useDiverseCarrierCertificationsCardStyles()

    const existingCertificationCodes = certifications.map(({ code }) => code)

    const [currentCertification, setCurrentCertification] = useState<TCertificationSchema>()

    const { createToggleHandler, modalState } = useModalState({
      certificationModal: false,
    })
    const { certificationModal: isCertificationsModalOpen } = modalState || {}
    const toggleCertificationModalHandler = createToggleHandler('certificationModal')

    const closeModal = useCallback(() => {
      setCurrentCertification(undefined)
      toggleCertificationModalHandler()
    }, [toggleCertificationModalHandler])

    const editCertificationHandler = (certification: ICertificationFragment) => {
      setCurrentCertification(getCertificationDefaultValues(certification))
      toggleCertificationModalHandler()
    }
    const { code: certificationCode } = currentCertification || {}
    const isEditing = Boolean(certificationCode)
    const { deleteHandler, loading, submitHandler } = useDiverseCarrierCertificationsSubmitter(
      motorCarrierId,
      isEditing,
      certificationCode
    )

    return (
      <Card>
        <div className={classes.header}>
          <Typography variant='h3'>Diverse Carrier Certifications</Typography>
          <Typography variant='body2'>
            Becoming a certified diverse business can give your company access to numerous benefits.
            Add any diverse carrier certifications you’ve achieved.
          </Typography>
        </div>
        {certifications.map(certification => {
          const { code, id, isVerified, issuingOrganization, renewalDate } = certification || {}
          return (
            <div key={`carrier-certification-${id}`} className={classes.certification}>
              <div>
                <Typography component='div' variant='subtitle1'>
                  {CERTIFICATION_CODE_LABELS[code]}
                </Typography>
                <Typography component='div' variant='body2'>
                  {issuingOrganization && <span>{issuingOrganization}</span>}
                  {issuingOrganization && renewalDate && <span> - </span>}
                  {renewalDate && <span>Renews {formatDate(renewalDate, 'monthDateFormat')}</span>}
                </Typography>
              </div>
              <div className={classes.certificationEdit}>
                {isVerified && (
                  <Tag label='Verified' tagStyle={TAG_STYLE.success} size={TAG_SIZE.xsmall} />
                )}
                {!isVerified && (
                  <div className={classes.iconButtonWrapper}>
                    <IconButton
                      Icon={EditIcon}
                      onClick={() => editCertificationHandler(certification)}
                      size='large'
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
        <div className={certifications.length > 0 ? classes.topBorder : ''}>
          <Button variant='text' onClick={toggleCertificationModalHandler}>
            Add Certifications
          </Button>
        </div>
        {isCertificationsModalOpen && (
          <CertificationModal
            existingCertificationCodes={existingCertificationCodes}
            defaultValues={currentCertification}
            onClose={closeModal}
            onSubmit={submitHandler}
            onDelete={deleteHandler}
            loading={loading}
          />
        )}
      </Card>
    )
  }
)

export default DiverseCarrierCertificationsCard
